// Standard library imports
import React, { useEffect, useState } from 'react';

// External library imports
import { toast } from 'react-toastify';
import moment from 'moment';
import lodashGet from 'lodash/get';
import { UilInfoCircle, UilImport } from '@iconscout/react-unicons';

// Internal Module Imports
import { useLoader, useUserRole } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import classes from '../Economics.module.css';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import Table from '../../../../components/Table/Table';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import { DownloadAsExcel } from '../../../../utils/downloadAsExcel';
import { RootEconomicsService } from '../../../../services/BiddingService/Root/EconomicsService';
import { AdminEconomicsService } from '../../../../services/BiddingService/Admin/EconomicsService';

const Consumption = ({ data, setOpen }) => {
    const [startLoader, stopLoader] = useLoader();
    const { isRootUser } = useUserRole();
    const service = isRootUser ? RootEconomicsService : AdminEconomicsService;
    const [consumptionData, setConsumptionData] = useState([]);
    const [hourlyDataModal, setHourlyDataModal] = useState({ status: false, data: [] });
    const [hourlyData, setHourlyData] = useState([]);

    useEffect(() => {
        if (data.customerId) getConsumption();
    }, [data.customerId]);

    useEffect(() => {
        if (lodashGet(hourlyDataModal, ['data', 'hourlyData'], []).length) getHourlyData();
    }, [hourlyDataModal.status]);

    const getConsumption = () => {
        const params = {
            startDate: moment(data.dateRange.startDate).format('YYYY-MM-DD'),
            endDate: moment(data.dateRange.endDate).format('YYYY-MM-DD'),
            customerId: data.customerId,
            market: data.market,
        };

        service.GetConsumption(params, startLoader, handleSuccess, handleError, stopLoader);
    };

    const handleSuccess = ({ data }) => {
        const proccesedData = lodashGet(data, ['data'], []).map((el) => ({
            date: momentTimeFormater(el.date).format('YYYY-MM-DD'),
            instance: el.instance,
            underDelivery: Number((el.underDelivery / 1000 || 0).toFixed(4)),
            overDelivery: Number((el.overDelivery / 1000 || 0).toFixed(4)),
            requested: Number((el.requested / 1000 || 0).toFixed(4)),
            activated: Number((el.activated / 1000 || 0).toFixed(4)),
            consumption: Number((el.consumption / 1000).toFixed(4)),
            dhCost: !isNaN(el?.dhCost) ? Number(el?.dhCost?.toFixed(2)) : '--',
            imbalanceCost: !isNaN(el?.imbalanceCost) ? Number(el?.imbalanceCost?.toFixed(2)) : '--',
            cost: !isNaN(el?.cost) ? Number(el?.cost?.toFixed(2)) : '--',
            energyRevenue: !isNaN(el?.energyRevenue) ? el?.energyRevenue : '--',
            action: (
                <CustomTooltip content={'Hourly data'}>
                    <div
                        onClick={() => {
                            setHourlyDataModal({
                                status: true,
                                data: { hourlyData: el?.hourlyData || [], fifteenMinutesData: el?.fifteenMinutesData || [] },
                            });
                        }}
                    >
                        <UilInfoCircle size={'1.2vw'} style={{ color: 'var(--color-primary)' }} />
                    </div>
                </CustomTooltip>
            ),
        }));

        setConsumptionData(proccesedData);
    };

    const handleError = (err) => {
        if (err && err.response) toast.error(err.response.data.message);
    };

    const getHourlyData = () => {
        const fifteenMinutesData = lodashGet(hourlyDataModal, ['data', 'fifteenMinutesData'], []);
        const proccesedData = lodashGet(hourlyDataModal, ['data', 'hourlyData'], []).map((el) => {
            const hour = el?.productName?.split('__')[1];
            const hourEntries = fifteenMinutesData.filter((con) => momentTimeFormater(con.timestamp).format('H') === hour);
            const sumBy = (key) => hourEntries.reduce((sum, con) => sum + (con?.[key] || 0), 0);

            return {
                hour: hour,
                consumption: Number((sumBy('consumption') / 1000).toFixed(4)),
                underDelivery: Number((sumBy('underDelivery') / 1000).toFixed(4)),
                overDelivery: Number((sumBy('overDelivery') / 1000).toFixed(4)),
                requested: Number((sumBy('requested') / 1000).toFixed(4)),
                activated: Number((sumBy('activated') / 1000).toFixed(4)),
                dhCost: !isNaN(el?.dhCost) ? Number(el?.dhCost?.toFixed(2)) : '--',
                cost: !isNaN(el?.cost) ? Number(el?.cost?.toFixed(2)) : '--',
                energyRevenue: !isNaN(el?.price) ? el?.price : '--',
                imbalanceCost: !isNaN(el?.imbalanceCost) ? Number(el?.imbalanceCost?.toFixed(2)) : '--',
            };
        });
        setHourlyData(proccesedData);
    };

    const getDynamicColsData = (data) => {
        const temp = {
            energyRevenue: [],
        };
        data.map((el) => {
            if ((!isNaN(el.dhCost) ? el.dhCost : 0) + (!isNaN(el?.cost) ? el?.cost : 0) > el.energyRevenue)
                temp.energyRevenue.push(<div style={{ color: 'red' }}>{el.energyRevenue}</div>);
            else temp.energyRevenue.push(<div>{el.energyRevenue}</div>);
        });
        return temp;
    };

    const handleDataExport = (exportData = [], headers = []) => {
        const excelData = exportData?.map((item) => ({
            ...(item?.date ? { date: item.date } : { hour: item.hour }),
            ...(item?.instance ? { instance: item.instance } : {}),
            underDelivery: item.underDelivery,
            overDelivery: item.overDelivery,
            requested: item.requested,
            activated: item.activated,
            consumption: item.consumption,
            energyRevenue: item.energyRevenue,
            dhCost: item.dhCost,
            cost: item.cost || 0,
            imbalanceCost: item?.imbalanceCost || 0,
        }));

        if (excelData.length) DownloadAsExcel(excelData, `Energy revenue and consumption (${lodashGet(data, ['customer'], '')})`, headers);
        else handleError({ response: { data: { message: 'No data to export!' } } });
    };

    return (
        <div className={classes.ConsumptionModal} style={{ width: '80vw' }}>
            <div className={classes.ConsumptionModalHeader}>
                <Typography content={`Consumption (${lodashGet(data, ['customer'], '')})`} />
                <div
                    onClick={() =>
                        handleDataExport(consumptionData, [
                            'Date',
                            'Instance',
                            'Underdelivered (MWh)',
                            'Overdelivered (MWh)',
                            'Requested (MWh)',
                            'Activated (MWh)',
                            'Consumption (MWh)',
                            'Energy Revenue (€)',
                            'Datahub Cost (€)',
                            'Cost (€)',
                            'Imbalance Cost (€)',
                        ])
                    }
                    style={{ marginBottom: '0.5vh' }}
                >
                    <UilImport
                        size="1.5vw"
                        style={{
                            cursor: 'pointer',
                            marginTop: '0.4vw',
                            color: 'var(--color-primary)',
                        }}
                    />
                </div>
            </div>
            <div>
                <Table
                    data={consumptionData}
                    head={[
                        'Date',
                        'Instance',
                        'Under del (MWh)',
                        'Over del (MWh)',
                        'Requested (MWh)',
                        'Activated (MWh)',
                        'Consumption (MWh)',
                        'EAM Revenue (€)',
                        'DH Cost (€)',
                        'Cost (€)',
                        'Imb Cost (€)',
                        'Action',
                    ]}
                    keys={[
                        'date',
                        'instance',
                        'underDelivery',
                        'overDelivery',
                        'requested',
                        'activated',
                        'consumption',
                        'energyRevenue',
                        'dhCost',
                        'cost',
                        'imbalanceCost',
                        'action',
                    ]}
                    dynamicStyleCols={getDynamicColsData(consumptionData)}
                    dynamicStyleKeys={['energyRevenue']}
                />
            </div>
            {hourlyDataModal.status && (
                <ModalComponent isOpen={hourlyDataModal.status} setOpen={() => setHourlyDataModal({ status: false, data: [] })}>
                    <div className={classes.ConsumptionModal} style={{ width: '75vw' }}>
                        <div className={classes.ConsumptionModalHeader}>
                            <Typography content={`Hourly Consumption `} />
                            <div
                                onClick={() =>
                                    handleDataExport(hourlyData, [
                                        'Hour',
                                        'Underdelivered (MWh)',
                                        'Overdelivered (MWh)',
                                        'Requested (MWh)',
                                        'Activated (MWh)',
                                        'Consumption (MWh)',
                                        'Energy Revenue (€)',
                                        'Datahub Cost (€)',
                                        'Cost (€)',
                                        'Imbalance Cost (€)',
                                    ])
                                }
                                style={{ marginBottom: '0.5vh' }}
                            >
                                <UilImport
                                    size="1.5vw"
                                    style={{
                                        cursor: 'pointer',
                                        marginTop: '0.4vw',
                                        color: 'var(--color-primary)',
                                    }}
                                />
                            </div>
                        </div>
                        <Table
                            data={hourlyData}
                            head={[
                                'Hour',
                                'Under del (MWh)',
                                'Over del (MWh)',
                                'Requested (MWh)',
                                'Activated (MWh)',
                                'Consumption (MWh)',
                                'EAM Revenue (€)',
                                'DH Cost (€)',
                                'Cost (€)',
                                'Imb Cost (€)',
                            ]}
                            keys={['hour', 'underDelivery', 'overDelivery', 'requested', 'activated', 'consumption', 'energyRevenue', 'dhCost', 'cost', 'imbalanceCost']}
                            dynamicStyleCols={getDynamicColsData(hourlyData)}
                            dynamicStyleKeys={['energyRevenue']}
                        />
                    </div>
                </ModalComponent>
            )}
        </div>
    );
};

export default Consumption;
